import { format } from 'date-fns';

/**
 * The `from`/`to` pair GET /invoices' month window takes (see the API route's
 * doc comment). Both are `YYYY-MM` shorthand — the API expands each into that
 * calendar month's Vietnam (+07:00) bounds (THI-185: real timestamps bucket by
 * VN wall-clock months, matching how every date is displayed), and treats `to`
 * as the month AFTER the window's last month (exclusive), so `from === to`
 * means "exactly this one month."
 * Never a literal ISO date here: the FE only ever windows by whole months.
 */
export interface InvoiceWindow {
  from: string;
  to: string;
  /** Picked-month (archival) view: strictly the window, no outstanding union.
   *  Absent on the default view, where unpaid invoices always ride along. */
  strict?: true;
}

const MONTH_FORMAT = 'yyyy-MM';

/** The `YYYY-MM` of `now` — the invoices page's default month. */
export function currentInvoiceMonth(now: Date): string {
  return format(now, MONTH_FORMAT);
}

/**
 * The invoices page's default view: THIS calendar month only, NOT strict, so
 * unpaid invoices from earlier months still ride along (the "tiền chưa thu
 * thì không bao giờ bị giấu" rule, Johnny 2026-08-18). Was "this month + the
 * two before" until 2026-09-04, when a teacher asked for the current month
 * alone: three months of paid rows buried the month being worked on. Call
 * with `new Date()` at render time; the result only changes at a month
 * boundary, which a fresh render naturally picks up.
 */
export function defaultInvoiceWindow(now: Date): InvoiceWindow {
  const month = currentInvoiceMonth(now);
  return { from: month, to: month };
}

/**
 * Expands a single picked `YYYY-MM` (from the native `<input type="month">`)
 * into a window covering exactly that month — `from` and `to` both set to it,
 * relying on the API's "to is exclusive-next-month" rule to bound it above.
 */
export function monthToWindow(month: string): InvoiceWindow {
  // strict: a deliberately picked month is an archival question ("what was
  // billed in February") — an out-of-month PENDING invoice showing up there
  // reads as a broken filter. The outstanding-union belongs to the default
  // view only (Johnny, prod smoke 2026-08-18).
  return { from: month, to: month, strict: true };
}
